'use client'

import { isbot } from 'isbot'

import { usePathname } from 'next/navigation'

import useCookiesConsent from '@hooks/useCookiesConsent'
import { useEffect } from 'react'

const SCRIPT_ID = 'li_px'
const PARTNER_ID = '10002364'

// LinkedIn's Ads Agreement forbids the Insight Tag on pages that collect sensitive data. These are the
// product surfaces where jobseekers enter résumés and personal details. Adding a route here is how you
// keep the tag off a new product page; the test in linkedin-insight-tag-routes.test.ts fails until any
// new route directory is classified one way or the other.
const PRODUCT_SEGMENTS = ['jobs', 'search', 'email', 'swagger']

const LOCALE_PATTERN = /^[a-z]{2}$/

export function isMarketingPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean)
  // Only strip the first segment when it looks like a locale. `/jobs` at the root is a product path,
  // not a locale — discarding its first segment unconditionally waved it straight through the gate.
  const afterLocale = LOCALE_PATTERN.test(parts[0] ?? '') ? parts.slice(1) : parts
  // `/v1` is the parallel legacy site, so `/v1/jobs/...` is a job page too.
  const segments = afterLocale[0] === 'v1' ? afterLocale.slice(1) : afterLocale
  return !PRODUCT_SEGMENTS.includes(segments[0] ?? '')
}

function appendScript(): void {
  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.innerHTML = `_linkedin_partner_id="${PARTNER_ID}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s)})(window.lintrk);`
  document.head.appendChild(script)
}

export default function LinkedInInsightTag({ isProd }: { isProd: boolean }): null {
  const pathname = usePathname()
  const [areCookiesAccepted] = useCookiesConsent()

  useEffect(() => {
    if (!areCookiesAccepted || !isProd) {
      return
    }
    // Crawler hits on an advertising pixel degrade ad targeting. Our own smoke suite accepts cookies in
    // its beforeEach, so it reaches this point with a fully consented profile.
    if (isbot(navigator.userAgent)) {
      return
    }
    if (!isMarketingPath(pathname)) {
      return
    }
    if (document.getElementById(SCRIPT_ID)) {
      return
    }
    appendScript()
  }, [areCookiesAccepted, isProd, pathname])

  return null
}
